import React, { useState } from 'react';
import { Zap, BadgeCheck, Check, Star, ShieldCheck, Trophy, Users, Globe,} from 'lucide-react';
import { TbDeviceMobileCode } from "react-icons/tb";
import { MdSupportAgent } from "react-icons/md";
import { AiOutlineApi } from "react-icons/ai";
import FAQSection from '../FaqPage/FaqSection';
import CtaSection from '../CtaSection/CtaSection';

const stats = [
    {
        label: "Trusted Retailers",
        value: "50,000+",
        icon: <Users className="w-6 h-6" />,
        description: "Empowering small businesses across India."
    },
    {
        label: "Pincodes Covered",
        value: "18,000+",
        icon: <Globe className="w-6 h-6" />,
        description: "Present in almost every corner of the nation."
    },
    {
        label: "Experience",
        value: "5+ year",
        icon: <Trophy className="w-6 h-6" />,
        description: "5 years experience helping small businesses"
    },

];


const monthlyPlans = [
    { name: "Basic", price: "500", features: ["BBPS Access", "LIC Payments", "Basic Support"], popular: false },
    { name: "Advanced", price: "800", features: ["BBPS Access", "LIC Payments", "Daily Reporting"], popular: false },
    { name: "Premium", price: "1000", features: ["BBPS Access", "LIC Payments", "Advanced Reporting"], popular: true },
    { name: "Custom", price: "1500", features: ["BBPS Access", "LIC Payments", "Priority Support"], popular: false },
];

const yearlyPlans = [
    { name: "Starter Pro", price: "499", features: ["BBPS Access", "LIC Payments", "Basic Support"], popular: false },
    { name: "Business", price: "999", features: ["BBPS Access", "LIC Payments", "Daily Reporting"], popular: false },
    { name: "Elite", price: "1499", features: ["BBPS Access", "LIC Payments", "Advanced Reporting"], popular: true },
    { name: "Ultimate", price: "1899", features: ["BBPS Access", "LIC Payments", "Priority Support"], popular: false },
];


const features = [
    {
        title: "Free mobile apps",
        description: "Bumppy Payments is providing a software application designed to run on a mobile devices such as smartphone or tablet.",
        icon: <TbDeviceMobileCode size={38} />,
        color: "bg-blue-50 text-blue-600 border-blue-100"
    },
    {
        title: "Bumppy support",
        description: "To create a massive payment system that uses technology to distribute payments instantly in a perfect networked environment.",
        icon: <MdSupportAgent size={38} />,
        color: "bg-pink-50 text-pink-600 border-pink-100"
    },
    {
        title: "App integration",
        description: "Application integration is the process of enable individual applications for its specific purpose—to work with one another.",
        icon: <AiOutlineApi size={38} />,
        color: "bg-slate-50 text-[#000856] border-slate-200"
    }
];

export default function Pricing() {
    const [isYearly, setIsYearly] = useState(false);

    const currentPlans = isYearly ? yearlyPlans : monthlyPlans;

    return (
        <div>
            <section className="relative w-full bg-[#000856] pt-10 pb-20 md:pt-20 md:pb-32 overflow-hidden font-inter">


                <div className="absolute inset-0 z-0">
                    <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-blue-500/20 blur-[130px] rounded-full" />
                    <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-pink-500/10 blur-[110px] rounded-full" />

                    <div className="absolute inset-0 opacity-[0.03] [mask-image:radial-gradient(ellipse_at_center,white,transparent)]"
                        style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                </div>

                <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">


                    <div className="space-y-6 max-w-4xl mx-auto">

                        <h1 className="font-spacegrotesk text-white text-5xl md:text-7xl font-bold leading-tight tracking-tight">
                            Plans that scale with <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-300 to-pink-500">
                                Your Business
                            </span>
                        </h1>

                        <p className="text-blue-100/70 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
                            Choose a plan that fits your needs. No hidden charges, no long-term contracts.
                            Just simple, transparent pricing for retailers of all sizes.
                        </p>
                    </div>

                    
                    <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto pt-10 border-t border-white/5">
                        {[
                            { icon: <ShieldCheck className="w-5 h-5" />, text: "Secure Transactions" },
                            { icon: <Zap className="w-5 h-5" />, text: "Instant Settlements" },
                            { icon: <Globe className="w-5 h-5" />, text: "Pan-India Network" },
                            { icon: <BadgeCheck className="w-5 h-5" />, text: "Lifetime Support" }
                        ].map((item, i) => (
                            <div key={i} className="flex flex-col items-center space-y-3 group">
                                <div className="p-3 rounded-2xl bg-white/5 border border-white/10 group-hover:bg-white group-hover:text-blue-900 transition-all duration-300 text-pink-400">
                                    {item.icon}
                                </div>
                                <span className="text-blue-100/60 text-xs font-bold uppercase tracking-wider">{item.text}</span>
                            </div>
                        ))}
                    </div>

                   
                    

                </div>

                <style dangerouslySetInnerHTML={{
                    __html: `
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @keyframes float-slow {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(20px) rotate(5deg); }
          }
          .animate-float { animation: float 6s ease-in-out infinite; }
          .animate-float-slow { animation: float-slow 8s ease-in-out infinite; }
        `
                }} />
            </section>
            <div className="w-full py-24 relative overflow-hidden font-inter bg-slate-100">

                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/50 blur-[120px] rounded-full" />
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-pink-100/50 blur-[100px] rounded-full" />

                <div className="max-w-7xl mx-auto px-6 relative z-10">

                    <div className="flex items-center justify-center space-x-6 mb-20">
                        {[
                            { id: 1, label: 'Select', active: true },
                            { id: 2, label: 'Add', active: false },
                            { id: 3, label: 'Choose', active: false }
                        ].map((step, i) => (
                            <React.Fragment key={step.id}>
                                <div className={`flex items-center space-x-3 ${step.active ? 'opacity-100' : 'opacity-40'}`}>
                                    <span className={`h-10 w-10 rounded-full flex items-center justify-center font-bold border-2 ${step.active
                                        ? 'bg-pink-500 border-pink-500 text-white shadow-lg shadow-pink-200'
                                        : 'border-slate-300 text-slate-500'
                                        }`}>
                                        {step.id}
                                    </span>
                                    <span className="text-slate-900 font-bold tracking-wide">{step.label}</span>
                                </div>
                                {i < 2 && <div className="w-16 h-[1px] bg-slate-200" />}
                            </React.Fragment>
                        ))}
                    </div>


                    <div className="flex flex-col items-center mb-16 space-y-6">
                        <div className="flex items-center bg-slate-100 p-1.5 rounded-2xl border border-slate-200 shadow-inner">
                            <button
                                onClick={() => setIsYearly(false)}
                                className={`px-6 py-2 rounded-xl font-bold transition-all ${!isYearly ? 'bg-white text-[#000856] shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
                            >
                                Free Trial Mode
                            </button>
                            <button
                                onClick={() => setIsYearly(true)}
                                className={`px-6 py-2 rounded-xl font-bold transition-all ${isYearly ? 'bg-[#000856] text-white shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
                            >
                                Enterprise Mode
                            </button>
                        </div>
                        <p className="text-slate-500 text-sm font-medium">
                            {isYearly ? "✨ Showing Plans (Save 20% with Annual Billing)" : "⚡ Showing Standard Trial Plans"}
                        </p>
                    </div>


                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {currentPlans.map((plan, index) => (
                            <div
                                key={index}
                                className={`group relative flex flex-col p-8 rounded-[2.5rem] transition-all duration-500 border-2 ${plan.popular
                                    ? 'bg-white border-pink-500 shadow-[0_20px_50px_rgba(236,72,153,0.15)] scale-105 z-10'
                                    : 'bg-white border-slate-100 hover:border-blue-200 shadow-sm hover:shadow-xl'
                                    }`}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-pink-500 text-white px-6 py-1.5 rounded-full text-xs font-black uppercase tracking-widest flex items-center space-x-2">
                                        <Star className="w-3 h-3 fill-white" />
                                        <span>Most Popular</span>
                                    </div>
                                )}

                                <div className="mb-8">
                                    <h3 className="text-blue-600 font-bold uppercase tracking-[0.2em] text-xs mb-2">{plan.name}</h3>
                                    <div className="flex items-baseline space-x-1">
                                        <span className="text-slate-900 text-4xl font-black">₹{plan.price}</span>
                                        <span className="text-slate-400 font-medium">/{'mo'}</span>
                                    </div>
                                </div>

                                <a href='https://dashboard.bumppy.com/' >
                                <button className={`w-full py-4 rounded-2xl font-bold uppercase tracking-widest transition-all mb-10 flex items-center justify-center space-x-2 ${plan.popular
                                    ? 'bg-[#000856] text-white hover:bg-pink-600 shadow-lg shadow-blue-100'
                                    : 'bg-slate-100 text-slate-800 hover:bg-[#000856] hover:text-white border border-slate-200'
                                    }`}>
                                    <span>Choose Plan</span>
                                    <Zap className="w-4 h-4 fill-current" />
                                    </button>
                                    </a>

                                <div className="space-y-4">
                                    {plan.features.map((feature, i) => (
                                        <div key={i} className="flex items-center space-x-3 group/item">
                                            <div className="h-5 w-5 rounded-full bg-blue-50 flex items-center justify-center">
                                                <Check className="w-3 h-3 text-blue-600 group-hover/item:scale-125 transition-transform" />
                                            </div>
                                            <span className="text-slate-600 text-sm font-medium">{feature}</span>
                                        </div>
                                    ))}
                                </div>


                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <section className="py-20 bg-slate-100 relative">
                <div className="max-w-7xl mx-auto px-6 relative z-10">


                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <h2 className="font-spacegrotesk text-slate-900 text-3xl md:text-4xl font-bold mb-4">
                            All Plans <span className="text-pink-500">Include</span>
                        </h2>
                        <p className="text-slate-500 font-medium">
                            Everything you need to transform your shop into a digital hub.
                        </p>
                    </div>


                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {features.map((item, index) => (
                            <div
                                key={index}
                                className="group p-10 rounded-[2.5rem] bg-white border border-slate-100 shadow-sm hover:shadow-2xl hover:shadow-blue-900/5 transition-all duration-500 hover:-translate-y-2 relative overflow-hidden"
                            >

                                <div className="absolute -right-10 -top-10 w-32 h-32 bg-slate-50 rounded-full group-hover:scale-[3] transition-transform duration-700 -z-0" />

                                <div className="relative z-10">

                                    <div className={`h-16 w-16 rounded-2xl flex items-center justify-center mb-8 border transition-all duration-500 group-hover:scale-110 group-hover:shadow-lg ${item.color}`}>
                                        {item.icon}
                                    </div>


                                    <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-blue-600 transition-colors">
                                        {item.title}
                                    </h3>
                                    <p className="text-slate-500 leading-relaxed font-medium">
                                        {item.description}
                                    </p>
                                </div>


                                <div className="absolute bottom-0 left-0 h-1.5 w-0 bg-gradient-to-r from-blue-600 to-pink-500 group-hover:w-full transition-all duration-500" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 bg-white relative overflow-hidden">

                <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(#000856 1.5px, transparent 1.5px)', backgroundSize: '40px 40px' }} />

                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-16">


                        <div className="w-full lg:w-1/3 space-y-6 text-center lg:text-left">
                            <h2 className="font-spacegrotesk text-slate-900 text-4xl md:text-5xl font-bold leading-tight">
                                Devoted to <br />
                                your <span className="text-blue-600">success</span>
                            </h2>
                            <p className="text-slate-500 text-lg font-medium leading-relaxed">
                                We are on a mission to bridge the gap between rural India and digital financial services.
                            </p>
                            <div className="pt-4">
                                <a href='https://dashboard.bumppy.com/register'>
                                    <button className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-bold transition-all hover:bg-pink-600 hover:shadow-xl hover:shadow-pink-200">
                                        Join our Journey
                                    </button>
                                </a>
                            </div>
                        </div>


                        <div className="w-full lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-8">
                            {stats.map((stat, index) => (
                                <div
                                    key={index}
                                    className="group p-8 rounded-[2rem] bg-slate-50 border border-slate-100 transition-all duration-500 hover:bg-white hover:shadow-2xl hover:shadow-blue-900/5 hover:-translate-y-2"
                                >
                                    <div className="flex items-center space-x-4 mb-4">
                                        <div className="h-12 w-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-[#000856] group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 shadow-sm">
                                            {stat.icon}
                                        </div>
                                        <h4 className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">
                                            {stat.label}
                                        </h4>
                                    </div>

                                    <div className="space-y-2">
                                        <span className="text-4xl font-black text-slate-900 tracking-tight">
                                            {stat.value}
                                        </span>
                                        <p className="text-slate-500 text-sm font-medium leading-relaxed">
                                            {stat.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                    </div>
                </div>
            </section>
            <FAQSection />
            <CtaSection />
        </div >
    );
};

